import "dayjs/locale/hr";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { reloadAppAsync } from "expo";
import { createIntl, createIntlCache } from "react-intl";
import { Platform } from "react-native";
import { constants } from "~/constants";
import { assert } from "~/utils/assert";
import { SupportedLanguage } from "./SupportedLanguage";
import { getLanguageFromURL } from "./getLanguageFromURL";
import { getPreferredLanguage } from "./getPreferredLanguage";
import { getSystemLanguage } from "./getSystemLanguage";
import { getTranslations } from "./getTranslations";
import { polyfillIntlGetCanonicalLocales } from "./polyfillIntlGetCanonicalLocales";
import { polyfillIntlLocale } from "./polyfillIntlLocale";
import { polyfillIntlPluralRules } from "./polyfillIntlPluralRules";
import type { IntlShape } from "react-intl";

declare global {
  const intl: IntlShape & {
    changeLanguage: (newLocale: SupportedLanguage) => Promise<void>;
    shouldRedirect: boolean;
  };
}

async function getLocaleInfo() {
  if (Platform.OS === "web") {
    const localeFromURL = getLanguageFromURL() as SupportedLanguage;
    const preferredLocale = getPreferredLanguage() as SupportedLanguage;
    const rawAreRedirectsDisabled = await AsyncStorage.getItem(
      constants.ASYNC_STORAGE_KEYS.DISABLE_LANGUAGE_REDIRECTS,
    );
    const areRedirectsDisabled = !!rawAreRedirectsDisabled;

    // If redirects are disabled (user manually selected language using language picker),
    // we use the language from the URL. Otherwise, if the user didn't manually select a language,
    // we check the language from the browser. If the browser language is the same as the one from the URL,
    // we use the language from the URL. Otherwise, we use the browser language.
    // This logic ensures that the correct language is used based on user preferences and browser settings.
    const locale = areRedirectsDisabled
      ? localeFromURL
      : localeFromURL === preferredLocale
        ? localeFromURL
        : preferredLocale;

    return {
      locale,
      shouldRedirect: areRedirectsDisabled
        ? false
        : localeFromURL !== preferredLocale,
      async changeLanguage(newLocale: SupportedLanguage) {
        const localeList = Object.values(SupportedLanguage);

        const localeToHrefMap: Record<string, string> = {};
        for (const locale of localeList) {
          const pathPartList = window.location.href.split("/");

          // First we want to remove the langauge from the URL
          // and normalize it to the default language
          // Example: http://localhost/hr/terms-of-services > http://localhost/terms-of-services
          if (pathPartList.length >= 4) {
            const lang = pathPartList.at(3);
            assert(lang !== undefined, "No lang");

            if (localeList.includes(lang as SupportedLanguage)) {
              pathPartList.splice(3, 1);
            }
          }
          // At this point we normalized the URL in pathPartList to the default language

          // Now we want to add the new language to the URL
          if (locale !== intl.defaultLocale) {
            pathPartList.splice(3, 0, locale);
          }

          // We remove the trailing slash
          if (pathPartList.at(-1) === "") {
            pathPartList.pop();
          }

          // If the URL is just the domain, we add a trailing slash
          if (pathPartList.length === 3) {
            pathPartList.push("");
          }

          const href = pathPartList.join("/");

          localeToHrefMap[locale] = href;
        }

        await AsyncStorage.setItem(
          constants.ASYNC_STORAGE_KEYS.DISABLE_LANGUAGE_REDIRECTS,
          "true",
        );
        const href = localeToHrefMap[newLocale];
        assert(href !== undefined, "No href");
        window.location.href = href;
      },
    };
  } else {
    const rawPreferredLocale = await AsyncStorage.getItem(
      constants.ASYNC_STORAGE_KEYS.PREFERRED_LANGUAGE_ON_MOBILE,
    );
    const supportedLanguageList = Object.values(SupportedLanguage);
    const preferredLocale = supportedLanguageList.includes(
      rawPreferredLocale as SupportedLanguage,
    )
      ? (rawPreferredLocale as SupportedLanguage)
      : null;

    const localeFromSystem = await getSystemLanguage();

    return {
      locale: preferredLocale ?? localeFromSystem,
      shouldRedirect: false, // We don't redirect on mobile
      async changeLanguage(newLocale: SupportedLanguage) {
        await AsyncStorage.setItem(
          constants.ASYNC_STORAGE_KEYS.PREFERRED_LANGUAGE_ON_MOBILE,
          newLocale,
        );
        await reloadAppAsync();
      },
    };
  }
}

export async function initializeI18n() {
  const localeInfo = await getLocaleInfo();

  // Polyfils needed for formatjs - plural rules -> https://formatjs.io/docs/polyfills/intl-pluralrules/
  await polyfillIntlGetCanonicalLocales();
  await polyfillIntlLocale();
  await polyfillIntlPluralRules(localeInfo.locale);
  const translations = await getTranslations(localeInfo.locale);

  const cache = createIntlCache();
  const createdIntl = createIntl(
    {
      messages: translations,
      defaultLocale: constants.DEFAULT_LANGUAGE,
      locale: localeInfo.locale,
      // @ts-ignore `changeLanguage` and `shouldRedirect` are not
      // part of the `IntlShape` type, but we add them here so they
      // are available globally on the `intl` object.
      changeLanguage: localeInfo.changeLanguage,
      shouldRedirect: localeInfo.shouldRedirect,
    },
    cache,
  );

  // We set the `intl` variable globally. This enables us to skip calling
  // `useIntl` in every component, and using `intl` in non-react components.
  // The only reason we would want to use `useIntl` is in case the language
  // changes dynamically in runtime. However, when we change the language, we
  // reload the app so we just set the `intl` variable globally.
  (globalThis as any).intl = createdIntl;
}
